import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card, DataStatus, Dialog, FormSubmitHandler, Switch} from 'platform/components';
import {Box, Center, Heading, HStack, Icon, Show, Stack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useCallback, useEffect, useMemo, useState} from 'react';
import {FieldErrors} from 'react-hook-form';
import {Trans} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';

import {noop, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {
  useGetBatchQuery,
  useListBatchVehiclesQuery,
  useRequestPriceReportMutation,
  useUpdateBatchVehicleMutation,
} from '../../../store/importApi';
import {handleApiError} from '../../../utils/handleApiError';
import {CreatePageContext} from '../../Create/components/CreatePageContext';
import {VehicleFormType} from '../../Create/types/VehicleFormType';
import {useGetRequestVehicleFromFormData} from '../../Create/utils/useGetRequestVehicleFromFormData';
import {isBatchComplete} from '../utils/isBatchComplete';
import {mapVehicleItemToImportVehicle} from '../utils/mapVehicleItemToImportVehicle';
import {requestVehicleToResolvedVehicle} from '../utils/requestVehicleToResolvedVehicle';
import {resolvedVehicleToRequestVehicle} from '../utils/resolvedVehicleToRequestVehicle';
import {EditEquipmentModal} from './EditEquipmentModal';
import {EditSpecificationModal} from './EditSpecificationModal';
import {ImportResultLoadingView} from './ImportResultLoadingView';
import {VehicleImportRow} from './VehicleImportRow';

const VEHICLES_PAGE_LIMIT = 1000;
const BATCH_POLLING_INTERVAL_MS = 10_000;

interface ImportResultViewProps extends TestIdProps {
  batchId: string;
}

export function ImportResultView(props: ImportResultViewProps) {
  const formatDate = useDateTimeFormatter();
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const [vehicleIdForEdit, setVehicleIdForEdit] = useState<string | null>(null);
  const [isGenerateConfirmOpen, setIsGenerateConfirmOpen] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(BATCH_POLLING_INTERVAL_MS);

  const {
    data: batch,
    isLoading: isBatchLoading,
    isError: isBatchError,
  } = useGetBatchQuery(isNotNil(props.batchId) ? {batchId: props.batchId} : skipToken, {
    pollingInterval,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    setPollingInterval(BATCH_POLLING_INTERVAL_MS);
  }, [props.batchId]);

  useEffect(() => {
    if (isBatchError || (isNotNil(batch) && isBatchComplete(batch.status))) {
      setPollingInterval(0);
    }
  }, [batch, isBatchError]);

  const {
    data: vehiclesResponse,
    isLoading: isVehiclesLoading,
    isError: isVehiclesError,
  } = useListBatchVehiclesQuery(
    isNotNil(props.batchId)
      ? {
          batchId: props.batchId,
          page: 1,
          limit: VEHICLES_PAGE_LIMIT,
        }
      : skipToken,
    {
      skip: isNil(batch) || !isBatchComplete(batch.status),
    }
  );

  const vehicles = useMemo(
    () => (vehiclesResponse?.data ?? []).map(mapVehicleItemToImportVehicle),
    [vehiclesResponse]
  );

  const importedAt = useMemo(() => {
    if (!batch?.createdAt) {
      return '';
    }
    const parsed = parseDate(batch.createdAt);
    return parsed ? formatDate('dateTimeShort', parsed) : '';
  }, [batch?.createdAt, formatDate]);

  const isWaitingForComplete = isNotNil(batch) && !isBatchComplete(batch.status);
  const isLoading = isBatchLoading || isVehiclesLoading;
  const isError = isBatchError || isVehiclesError;
  const importName = batch?.name;
  const filteredVehicles = showErrorsOnly
    ? vehicles.filter((vehicle) => vehicle.rowStatus !== 'success')
    : vehicles;

  const requestVehicleForEdit = useMemo(
    () =>
      resolvedVehicleToRequestVehicle(
        vehicleIdForEdit
          ? vehiclesResponse?.data?.find((v) => v.id === vehicleIdForEdit)?.resolvedVehicle
          : null
      ),
    [vehicleIdForEdit, vehiclesResponse]
  );

  const [updateBatchVehicle] = useUpdateBatchVehicleMutation();
  const [requestPriceReport, {isLoading: isRequestPriceReportLoading}] =
    useRequestPriceReportMutation();
  const getRequestVehicleFromFormData = useGetRequestVehicleFromFormData();

  const handleEditFormSubmit = useCallback<FormSubmitHandler<VehicleFormType>>(
    (data) => {
      if (isNil(vehicleIdForEdit)) {
        return Promise.resolve();
      }
      const requestVehicle = getRequestVehicleFromFormData(data);
      const resolvedVehicle = requestVehicleToResolvedVehicle(requestVehicle);
      return updateBatchVehicle({
        batchId: props.batchId,
        vehicleId: vehicleIdForEdit,
        updateVehicleRequest: {resolvedVehicle},
      })
        .unwrap()
        .then(() => {
          setIsEditModalOpen(false);
          setIsEquipmentModalOpen(false);
          setVehicleIdForEdit(null);
        })
        .catch(handleApiError);
    },
    [vehicleIdForEdit, props.batchId, getRequestVehicleFromFormData, updateBatchVehicle]
  );

  const handleEditFormInvalidSubmit = useCallback((errors: FieldErrors<VehicleFormType>) => {
    const errorNames = Object.keys(errors);
    if (errorNames.length > 0) {
      const input = document.querySelector(`[data-name=${errorNames[0]}]`);
      input?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }
  }, []);

  const handleToggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const errorCount = batch?.vehicleSummary?.errors ?? 0;
  const warningCount = batch?.vehicleSummary?.warnings ?? 0;
  const hasErroredVehicles = errorCount + warningCount > 0;

  const handleGeneratePriceReportsClick = useCallback(() => {
    if (isRequestPriceReportLoading) {
      return;
    }
    if (hasErroredVehicles) {
      setIsGenerateConfirmOpen(true);
      return;
    }
    requestPriceReport({batchId: props.batchId}).unwrap().catch(handleApiError);
  }, [hasErroredVehicles, isRequestPriceReportLoading, props.batchId, requestPriceReport]);

  const handleProceedAnyway = useCallback(() => {
    if (isRequestPriceReportLoading) {
      return;
    }
    requestPriceReport({batchId: props.batchId})
      .unwrap()
      .then(() => setIsGenerateConfirmOpen(false))
      .catch(handleApiError);
  }, [isRequestPriceReportLoading, props.batchId, requestPriceReport]);

  const batchTitle = isNotNil(batch)
    ? `${i18n.t('page.import.labels.batch')} • ${i18n.t('page.import.labels.vehicles', {count: batch.vehicleCount})}`
    : '';

  if (isWaitingForComplete && !isError) {
    return (
      <Box maxWidth={150} width="100%" data-testid={suffixTestId('wrapper', props)}>
        <ImportResultLoadingView
          importName={importName}
          data-testid={suffixTestId('loadingView', props)}
        />
      </Box>
    );
  }

  return (
    <>
      <Box width="100%" data-testid={suffixTestId('wrapper', props)}>
        <Card data-testid={suffixTestId('card', props)}>
          <Box padding={6}>
            <VStack spacing={6}>
              <DataStatus
                isLoading={isLoading}
                isError={isError}
                errorMessage={i18n.t('page.import.labels.loadError')}
                minHeight={20}
                data-testid={suffixTestId('dataStatus', props)}
              >
                <Stack
                  direction={['column', 'column', 'row']}
                  spacing={[2, 2, 4]}
                  justify={['flex-start', 'flex-start', 'space-between']}
                  align="flex-start"
                >
                  <Heading size={1}>{batchTitle}</Heading>
                  <VStack align="flex-end" spacing={1}>
                    <Text size="xSmall" color="secondary">
                      {i18n.t('page.import.labels.imported')}
                    </Text>
                    <Text size="xSmall">{importedAt}</Text>
                  </VStack>
                </Stack>

                <Switch
                  label={i18n.t('page.import.labels.showErrorsOnly')}
                  value={showErrorsOnly}
                  onChange={setShowErrorsOnly}
                  data-testid={suffixTestId('errorsOnlySwitch', props)}
                />

                <Box>
                  <Box
                    backgroundColor="palettes.neutral.10.100"
                    borderBottom="1px solid"
                    borderColor="palettes.neutral.30.100"
                    paddingVertical={2}
                    paddingHorizontal={4}
                  >
                    <HStack spacing={4} align="center">
                      <Box width={10}>
                        <Text size="xSmall" color="secondary" alternative>
                          {i18n.t('page.import.labels.rowNumber')}
                        </Text>
                      </Box>
                      <Text size="xSmall" color="secondary" alternative>
                        {i18n.t('page.import.labels.specification')}
                      </Text>
                    </HStack>
                  </Box>

                  {filteredVehicles.map((vehicle) => (
                    <VehicleImportRow
                      key={vehicle.id}
                      batchId={props.batchId}
                      vehicle={vehicle}
                      isExpanded={expandedIds.has(vehicle.id)}
                      onToggle={() => handleToggleRow(vehicle.id)}
                      onEditSpecification={() => {
                        setVehicleIdForEdit(vehicle.id);
                        setIsEditModalOpen(true);
                      }}
                      onEditEquipment={() => {
                        setVehicleIdForEdit(vehicle.id);
                        setIsEquipmentModalOpen(true);
                      }}
                      data-testid={suffixTestId(`vehicle-${vehicle.id}`, props)}
                    />
                  ))}
                </Box>

                <HStack justify="center">
                  <Button
                    variant="primary"
                    title={i18n.t('page.import.actions.generatePriceReports')}
                    suffix={`(${i18n.t('page.import.labels.vehicles', {count: batch?.vehicleCount ?? vehicles.length})})`}
                    isLoading={isRequestPriceReportLoading}
                    isDisabled={isRequestPriceReportLoading}
                    onClick={handleGeneratePriceReportsClick}
                    data-testid={suffixTestId('generatePriceReportsButton', props)}
                  />
                </HStack>
              </DataStatus>
            </VStack>
          </Box>
        </Card>
      </Box>

      <Dialog
        size="small"
        isOpen={isGenerateConfirmOpen}
        onClose={() => setIsGenerateConfirmOpen(false)}
        title={i18n.t('page.import.generatePriceReportConfirm.title')}
        buttons={[
          {
            variant: 'secondary',
            title: i18n.t('page.import.actions.reviewIssues'),
            onClick: () => setIsGenerateConfirmOpen(false),
            'data-testid': suffixTestId('generateConfirmReviewIssues', props),
          },
          {
            variant: 'primary',
            title: i18n.t('page.import.actions.proceedAnyway'),
            onClick: handleProceedAnyway,
            isLoading: isRequestPriceReportLoading,
            isDisabled: isRequestPriceReportLoading,
            'data-testid': suffixTestId('generateConfirmProceedAnyway', props),
          },
        ]}
        data-testid={suffixTestId('generatePriceReportConfirmDialog', props)}
      >
        <VStack spacing={3}>
          <Center>
            <Box
              backgroundColor="palettes.orange.10.100"
              borderRadius="circular"
              border="1px solid"
              borderColor="palettes.orange.30.100"
              width={12}
              height={12}
              data-testid={suffixTestId('generateConfirmWarningIcon', props)}
            >
              <Center height="100%">
                <Icon value="alert/warning" size={8} color="severity.warning" />
              </Center>
            </Box>
          </Center>
          <Show when={errorCount > 0}>
            <Text size="small">
              <Trans
                i18nKey="page.import.generatePriceReportConfirm.errorsNotPriced"
                values={{count: errorCount}}
                components={{bold: <Text key="bold" alternative inline size="small" />}}
              />
            </Text>
          </Show>
          <Show when={warningCount > 0}>
            <Text size="small">
              <Trans
                i18nKey="page.import.generatePriceReportConfirm.warningsCount"
                values={{count: warningCount}}
                components={{bold: <Text key="bold" alternative inline size="small" />}}
              />
            </Text>
          </Show>
        </VStack>
      </Dialog>

      {isEditModalOpen || isEquipmentModalOpen ? (
        <CreatePageContext
          key={vehicleIdForEdit ?? 'empty'}
          onSubmit={handleEditFormSubmit}
          onInvalidSubmit={handleEditFormInvalidSubmit}
          onDebouncedChange={noop}
          vehicle={requestVehicleForEdit}
          market={batch?.market}
        >
          <EditSpecificationModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSubmit={handleEditFormSubmit}
            onInvalidSubmit={handleEditFormInvalidSubmit}
            data-testid={suffixTestId('editSpecModal', props)}
          />
          <EditEquipmentModal
            isOpen={isEquipmentModalOpen}
            onClose={() => setIsEquipmentModalOpen(false)}
            onSubmit={handleEditFormSubmit}
            onInvalidSubmit={handleEditFormInvalidSubmit}
            data-testid={suffixTestId('editEquipmentModal', props)}
          />
        </CreatePageContext>
      ) : null}
    </>
  );
}
